import { useQuery } from "@tanstack/react-query";
import type { DraftField, FieldTypeSummary } from "@sga/shared";
import { apiGet } from "../lib/api.js";
import { MarkupEditor } from "./MarkupEditor.jsx";

/**
 * The right pane. Type-specific config is **generated**, not hand-written per field type
 * (docs/04-BUILD-PLAN.md Phase 4) — 44 bespoke panels would drift from the schemas the moment
 * anyone added a config key.
 *
 * The generator infers each control from the shape of the type's `defaultConfig`, which
 * `GET /field-types` already serves. That is a deliberate choice over shipping the Zod schema
 * itself: Zod schemas are not JSON-serialisable, so exposing their internals over the wire would
 * mean inventing a parallel description format and keeping *that* in sync. The default value's
 * runtime type — boolean, number, string, array of `{value,label}` — is enough to pick a control,
 * and it comes from the same source of truth the validator uses.
 */

const inputClass = "w-full rounded-md border px-2 py-1.5 text-xs";
const labelClass =
  "mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground";

const humanise = (key: string): string =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (character) => character.toUpperCase())
    .trim();

interface OptionEntry {
  value: string;
  label: string;
}

const isOptionList = (value: unknown): value is OptionEntry[] =>
  Array.isArray(value) &&
  value.every(
    (entry) => typeof entry === "object" && entry !== null && "value" in entry && "label" in entry,
  );

/**
 * Editor for `allowedStates` — which states a State/LGA question accepts.
 *
 * Generated controls cover every other config shape, but not this one: a bare `string[]` falls
 * through every branch below and rendered nothing at all, which is why the only way this had ever
 * been set was a seed script. Meanwhile the one restriction the field did offer was the JV's host
 * pair, hardcoded — so an Imo form could not be built at all, and the Edo form accepted Delta
 * answers (43 of them did arrive that way).
 *
 * Nothing ticked means every state, which is what the Scholarship programme needs.
 */
const StatesEditor = ({
  value,
  programmeAreaStates,
  onChange,
}: {
  value: string[];
  /** The programme's own areas, offered as one click because it is the usual answer. */
  programmeAreaStates: string[];
  onChange: (next: string[]) => void;
}): JSX.Element => {
  // Same query key as the preview pane's, so the two share one 24-hour-cached fetch.
  const statesQuery = useQuery({
    queryKey: ["ng-states"],
    queryFn: () =>
      apiGet<{ data: { code: string; name: string; geoZone: string; isSeplatHost: boolean }[] }>(
        "/public/lookup/states",
      ),
    staleTime: Infinity,
  });

  const states = statesQuery.data?.data ?? [];
  const selected = new Set(value);
  const toggle = (name: string): void => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    onChange([...next]);
  };

  const preset = (names: string[]): void => onChange(names);
  const hostStates = states.filter((state) => state.isSeplatHost).map((state) => state.name);

  return (
    <div>
      <span className={labelClass}>States this form accepts</span>
      <p className="mb-2 text-[11px] text-muted-foreground">
        {selected.size === 0
          ? "Every state — nothing is refused."
          : `${selected.size} selected. Anything else is refused, in the browser and again at submit.`}
      </p>

      <div className="mb-2 flex flex-wrap gap-1">
        {programmeAreaStates.length > 0 && (
          <button
            type="button"
            onClick={() => preset(programmeAreaStates)}
            className="rounded border px-1.5 py-0.5 text-[11px] hover:bg-muted"
          >
            This programme&rsquo;s areas
          </button>
        )}
        {hostStates.length > 0 && (
          <button
            type="button"
            onClick={() => preset(hostStates)}
            className="rounded border px-1.5 py-0.5 text-[11px] hover:bg-muted"
          >
            JV host states
          </button>
        )}
        <button
          type="button"
          onClick={() => preset([])}
          className="rounded border px-1.5 py-0.5 text-[11px] hover:bg-muted"
        >
          All states
        </button>
      </div>

      {statesQuery.isPending ? (
        <p className="text-[11px] text-muted-foreground">Loading states…</p>
      ) : (
        <div className="max-h-48 overflow-y-auto rounded border p-2">
          {states.map((state) => (
            <label key={state.code} className="flex items-center gap-2 py-0.5 text-xs">
              <input
                type="checkbox"
                checked={selected.has(state.name)}
                onChange={() => toggle(state.name)}
              />
              <span className="truncate">{state.name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

/** Editor for a `{ value, label }[]` config key — choice options, matrix columns, and so on. */
const OptionListEditor = ({
  value,
  onChange,
}: {
  value: OptionEntry[];
  onChange: (next: OptionEntry[]) => void;
}): JSX.Element => (
  <div className="space-y-1">
    {value.map((option, index) => (
      <div key={index} className="flex gap-1">
        <input
          className={inputClass}
          aria-label={`Option ${index + 1} label`}
          value={option.label}
          onChange={(event) => {
            const next = [...value];
            // Keep `value` in step with `label` only while they match — once someone has edited the
            // stored value deliberately, renaming the label must not silently invalidate answers
            // already submitted against it.
            const wasDerived = option.value === option.label;
            next[index] = {
              label: event.target.value,
              value: wasDerived ? event.target.value : option.value,
            };
            onChange(next);
          }}
        />
        <button
          type="button"
          className="rounded-md border px-2 text-xs hover:bg-secondary"
          aria-label={`Remove option ${option.label}`}
          onClick={() => onChange(value.filter((_, position) => position !== index))}
        >
          ×
        </button>
      </div>
    ))}
    <button
      type="button"
      className="w-full rounded-md border border-dashed px-2 py-1 text-xs hover:bg-secondary"
      onClick={() =>
        onChange([
          ...value,
          { value: `option_${value.length + 1}`, label: `Option ${value.length + 1}` },
        ])
      }
    >
      Add option
    </button>
  </div>
);

const ConfigControl = ({
  configKey,
  currentValue,
  defaultValue,
  enumOptions,
  onChange,
}: {
  configKey: string;
  currentValue: unknown;
  defaultValue: unknown;
  /** Fixed set of allowed values for this config key, from the field type's own Zod schema. */
  enumOptions?: readonly string[];
  onChange: (next: unknown) => void;
}): JSX.Element | null => {
  const id = `config-${configKey}`;
  const value = currentValue ?? defaultValue;

  // Checked first: a string config key with a fixed value set (e.g. image.align) is a dropdown
  // regardless of what its runtime type would otherwise infer.
  if (enumOptions && enumOptions.length > 0) {
    return (
      <div>
        <label htmlFor={id} className={labelClass}>
          {humanise(configKey)}
        </label>
        <select
          id={id}
          className={inputClass}
          value={typeof value === "string" ? value : enumOptions[0]}
          onChange={(event) => onChange(event.target.value)}
        >
          {enumOptions.map((option) => (
            <option key={option} value={option}>
              {humanise(option)}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (isOptionList(defaultValue) || isOptionList(value)) {
    return (
      <div>
        <span className={labelClass}>{humanise(configKey)}</span>
        <OptionListEditor
          value={isOptionList(value) ? value : []}
          onChange={(next) => onChange(next)}
        />
      </div>
    );
  }

  if (typeof defaultValue === "boolean") {
    return (
      <label className="flex items-center gap-2 text-xs">
        <input
          id={id}
          type="checkbox"
          checked={value === true}
          onChange={(event) => onChange(event.target.checked)}
        />
        {humanise(configKey)}
      </label>
    );
  }

  if (typeof defaultValue === "number") {
    return (
      <div>
        <label htmlFor={id} className={labelClass}>
          {humanise(configKey)}
        </label>
        <input
          id={id}
          type="number"
          className={inputClass}
          value={typeof value === "number" ? value : ""}
          onChange={(event) =>
            onChange(event.target.value === "" ? undefined : Number(event.target.value))
          }
        />
      </div>
    );
  }

  if (typeof defaultValue === "string" || defaultValue === undefined) {
    // Authored prose, not a setting: a terms body or a rich-text block is a document, and a
    // four-row plain textarea is why one shipped to applicants as a single run-on paragraph.
    const isLong = configKey === "bodyText" || configKey === "html";
    return (
      <div>
        <label htmlFor={id} className={labelClass}>
          {humanise(configKey)}
        </label>
        {isLong ? (
          <MarkupEditor
            id={id}
            value={typeof value === "string" ? value : ""}
            onChange={onChange}
            placeholder={
              configKey === "bodyText"
                ? "1. Attendance at all scheduled sessions is mandatory.\n2. Participants must maintain a professional demeanour."
                : undefined
            }
          />
        ) : (
          <input
            id={id}
            className={inputClass}
            value={typeof value === "string" ? value : ""}
            onChange={(event) => onChange(event.target.value)}
          />
        )}
      </div>
    );
  }

  // Arrays of plain strings (e.g. name.titleOptions) — edited as one comma-separated line.
  if (Array.isArray(defaultValue)) {
    return (
      <div>
        <label htmlFor={id} className={labelClass}>
          {humanise(configKey)}
        </label>
        <input
          id={id}
          className={inputClass}
          value={Array.isArray(value) ? (value as string[]).join(", ") : ""}
          onChange={(event) =>
            onChange(
              event.target.value
                .split(",")
                .map((entry) => entry.trim())
                .filter(Boolean),
            )
          }
        />
      </div>
    );
  }

  return null;
};

/**
 * Widths a coordinator actually reaches for, on the 12-column grid the renderer lays out. The raw
 * 1–12 is exposed as fractions rather than column counts because "6" means nothing to someone
 * arranging a form, while "Half" does.
 */
const WIDTH_CHOICES: readonly { span: number; label: string }[] = [
  { span: 12, label: "Full width" },
  { span: 6, label: "Half" },
  { span: 4, label: "One third" },
  { span: 8, label: "Two thirds" },
  { span: 3, label: "One quarter" },
  { span: 9, label: "Three quarters" },
];

/**
 * Where the field sits on the page — the control that was missing entirely, leaving `rowGroup` and
 * `widthSpan` reachable only through a menu item that half-worked (see builder/rowGroups.ts).
 */
const LayoutControls = ({
  field,
  canJoin,
  sharesRow,
  onJoinRow,
  onSplitRow,
  onChange,
}: {
  field: DraftField;
  canJoin: boolean;
  sharesRow: boolean;
  onJoinRow: () => void;
  onSplitRow: () => void;
  onChange: (next: DraftField) => void;
}): JSX.Element => (
  <fieldset className="space-y-2 border-t pt-3">
    <legend className={labelClass}>Layout</legend>

    <label className="flex items-start gap-2 text-xs">
      <input
        type="checkbox"
        checked={sharesRow}
        disabled={!canJoin && !sharesRow}
        onChange={(event) => (event.target.checked ? onJoinRow() : onSplitRow())}
      />
      <span>
        Place on the same row as the field above
        {!canJoin && !sharesRow && (
          <span className="block text-[11px] text-muted-foreground">
            This is the first field in its section, so there is no row to join.
          </span>
        )}
      </span>
    </label>

    <div>
      <label htmlFor="prop-width" className={labelClass}>
        Width
      </label>
      <select
        id="prop-width"
        className={inputClass}
        value={field.widthSpan}
        onChange={(event) => onChange({ ...field, widthSpan: Number(event.target.value) })}
      >
        {WIDTH_CHOICES.map((choice) => (
          <option key={choice.span} value={choice.span}>
            {choice.label}
          </option>
        ))}
      </select>
      <p className="mt-1 text-[11px] text-muted-foreground">
        {sharesRow
          ? "Fields sharing a row split it evenly until you set a width here."
          : "Applies once this field shares a row with another."}{" "}
        Narrow screens always show one field per row.
      </p>
    </div>
  </fieldset>
);

export const PropertiesPanel = ({
  field,
  fieldType,
  profileAttributes,
  programmeAreaStates,
  canJoinRow,
  sharesRow,
  onJoinRow,
  onSplitRow,
  onChange,
}: {
  field: DraftField | null;
  fieldType: FieldTypeSummary | undefined;
  profileAttributes: { key: string; label: string; category: string }[];
  /** The programme's own areas, so a State/LGA field can be set to them in one click. */
  programmeAreaStates: string[];
  canJoinRow: boolean;
  sharesRow: boolean;
  onJoinRow: () => void;
  onSplitRow: () => void;
  onChange: (next: DraftField) => void;
}): JSX.Element => {
  if (!field || !fieldType) {
    return (
      <aside className="w-72 shrink-0 border-l bg-card p-4" aria-label="Field properties">
        <p className="text-xs text-muted-foreground">
          Select a field on the canvas to edit its properties.
        </p>
      </aside>
    );
  }

  const setConfig = (configKey: string, next: unknown): void =>
    onChange({ ...field, config: { ...field.config, [configKey]: next } });

  return (
    <aside
      className="flex w-72 shrink-0 flex-col overflow-y-auto border-l bg-card"
      aria-label="Field properties"
    >
      <div className="border-b p-3">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
          {fieldType.label}
        </p>
        <p className="font-mono text-xs">{field.key}</p>
      </div>

      <div className="space-y-4 p-3">
        <div>
          <label htmlFor="prop-label" className={labelClass}>
            Label
          </label>
          <input
            id="prop-label"
            className={inputClass}
            value={field.label}
            onChange={(event) => onChange({ ...field, label: event.target.value })}
          />
        </div>

        <div>
          <label htmlFor="prop-help" className={labelClass}>
            Help text
          </label>
          <input
            id="prop-help"
            className={inputClass}
            value={field.helpText ?? ""}
            onChange={(event) => onChange({ ...field, helpText: event.target.value })}
          />
        </div>

        <div>
          <label htmlFor="prop-visibility" className={labelClass}>
            Visibility
          </label>
          <select
            id="prop-visibility"
            className={inputClass}
            value={field.visibility}
            onChange={(event) =>
              onChange({ ...field, visibility: event.target.value as DraftField["visibility"] })
            }
          >
            <option value="visible">Visible</option>
            <option value="hidden">Hidden</option>
            <option value="admin_only">Admin only</option>
          </select>
        </div>

        {/* Universal flags — docs/01-PRODUCT-SPEC.md §3.1's table of universal field properties. */}
        <fieldset className="space-y-1.5">
          <legend className={labelClass}>Data handling</legend>
          {(
            [
              ["isRequired", "Required"],
              ["isPii", "Personal data (masked from staff without view_pii)"],
              ["isSensitive", "Sensitive (encrypted, excluded from search)"],
              ["isSearchable", "Include in global search"],
              ["isDedupeKey", "Use for duplicate detection"],
            ] as const
          ).map(([flag, description]) => (
            <label key={flag} className="flex items-start gap-2 text-xs">
              <input
                type="checkbox"
                checked={Boolean(field[flag])}
                onChange={(event) => onChange({ ...field, [flag]: event.target.checked })}
              />
              <span>{description}</span>
            </label>
          ))}
        </fieldset>

        <LayoutControls
          field={field}
          canJoin={canJoinRow}
          sharesRow={sharesRow}
          onJoinRow={onJoinRow}
          onSplitRow={onSplitRow}
          onChange={onChange}
        />

        {/* Profile binding — what makes cross-year prefill work (docs/01-PRODUCT-SPEC.md §6.4). */}
        {fieldType.supportsProfileBinding && (
          <div>
            <label htmlFor="prop-attribute" className={labelClass}>
              Profile attribute
            </label>
            <input
              list="profile-attributes"
              id="prop-attribute"
              className={inputClass}
              placeholder="e.g. person.first_name"
              value={field.profileAttributeKey ?? ""}
              onChange={(event) =>
                onChange({ ...field, profileAttributeKey: event.target.value || null })
              }
            />
            <datalist id="profile-attributes">
              {profileAttributes.map((attribute) => (
                <option key={attribute.key} value={attribute.key}>
                  {attribute.label} ({attribute.category})
                </option>
              ))}
            </datalist>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Binding this field carries the answer across forms and years.
            </p>
          </div>
        )}

        {/* Generated from the field type's own defaultConfig — see the note at the top of this file. */}
        {Object.keys(fieldType.defaultConfig).length > 0 && (
          <div className="space-y-3 border-t pt-3">
            <p className={labelClass}>{fieldType.label} settings</p>
            {Object.entries(fieldType.defaultConfig).map(([configKey, defaultValue]) =>
              configKey === "allowedStates" ? (
                <StatesEditor
                  key={configKey}
                  value={
                    Array.isArray(field.config[configKey])
                      ? (field.config[configKey] as string[])
                      : []
                  }
                  programmeAreaStates={programmeAreaStates}
                  onChange={(next) => setConfig(configKey, next)}
                />
              ) : (
                <ConfigControl
                  key={configKey}
                  configKey={configKey}
                  currentValue={field.config[configKey]}
                  defaultValue={defaultValue}
                  enumOptions={fieldType.configOptions[configKey]}
                  onChange={(next) => setConfig(configKey, next)}
                />
              ),
            )}
          </div>
        )}
      </div>
    </aside>
  );
};
